import {
    EntityPlanAttribute,
    GetPaperPlanMetadataOutput,
    RetailPriceAttributesEnum
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';

import { ColumnState } from 'ag-grid-community';
import { isUndefined } from 'lodash';
import { BundlePricingSubAttribute } from 'src/constants';
import { IBundlePricingRowData } from 'src/types/IBundlePricing';

export const mapPaperPlanMetadataToBundlePricingRowData = (
    response: GetPaperPlanMetadataOutput
): IBundlePricingRowData[] => {
    if (isUndefined(response.planMetadata)) {
        return [];
    }

    return response.planMetadata.reduce((planAccumulator, currentPlan) => {
        if (isUndefined(currentPlan.territoryAttributeSet)) {
            return planAccumulator;
        }

        const rowsForOnePlan = Object.keys(currentPlan.territoryAttributeSet).reduce(
            (territoryAccumulator, currentTerritory) => {
                // Skip global territory attributes
                if (currentTerritory === '00') {
                    return territoryAccumulator;
                }

                const rowsForOneTerritory: IBundlePricingRowData[] = [];
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                currentPlan.territoryAttributeSet![currentTerritory].forEach((attributeSet) => {
                    const attributesGroupedByName: Map<string, EntityPlanAttribute> = new Map(
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        attributeSet.attributes!.map((attribute) => [attribute.attribute!, attribute])
                    );

                    const planName = currentPlan.paperPlan?.paperPlanName ?? '';
                    const currency =
                        attributesGroupedByName.get(RetailPriceAttributesEnum.RETAIL_PRICE_CURRENCY)?.value ?? '';
                    const startDate = attributeSet.startDate?.toString() ?? '';
                    const endDate = attributeSet.endDate?.toString() ?? '';
                    const lastUpdated =
                        currentPlan.paperPlan?.changeDate?.toString() ??
                        currentPlan.paperPlan?.creationDate?.toString() ??
                        '';
                    const updatedBy = currentPlan.paperPlan?.changedBy ?? currentPlan.paperPlan?.createdBy ?? '';
                    const isDeleted = currentPlan.paperPlan?.isDeleted ?? false;
                    const isDraft = currentPlan.paperPlan?.isDraft ?? true;

                    const generateRow = (
                        value: string | undefined,
                        subAttribute: BundlePricingSubAttribute
                    ): IBundlePricingRowData => ({
                        territory: currentTerritory,
                        planName: planName,
                        planSubAttribute: subAttribute,
                        currency: currency,
                        price: value ?? '',
                        startDate: startDate,
                        endDate: endDate,
                        lastUpdated: lastUpdated,
                        updatedBy: updatedBy,
                        isDeleted: isDeleted,
                        isDraft: isDraft
                    });

                    if (attributesGroupedByName.has(RetailPriceAttributesEnum.STANDALONE_MUSIC_RETAIL_PRICE)) {
                        rowsForOneTerritory.push(
                            generateRow(
                                attributesGroupedByName.get(RetailPriceAttributesEnum.STANDALONE_MUSIC_RETAIL_PRICE)
                                    ?.value,
                                BundlePricingSubAttribute.NON_PRIME
                            )
                        );
                    }

                    if (
                        attributesGroupedByName.has(
                            RetailPriceAttributesEnum.PRIME_DISCOUNT_STANDALONE_MUSIC_RETAIL_PRICE
                        )
                    ) {
                        rowsForOneTerritory.push(
                            generateRow(
                                attributesGroupedByName.get(
                                    RetailPriceAttributesEnum.PRIME_DISCOUNT_STANDALONE_MUSIC_RETAIL_PRICE
                                )?.value,
                                BundlePricingSubAttribute.PRIME
                            )
                        );
                    }

                    if (attributesGroupedByName.has(RetailPriceAttributesEnum.IOS_STANDALONE_MUSIC_RETAIL_PRICE)) {
                        rowsForOneTerritory.push(
                            generateRow(
                                attributesGroupedByName.get(RetailPriceAttributesEnum.IOS_STANDALONE_MUSIC_RETAIL_PRICE)
                                    ?.value,
                                BundlePricingSubAttribute.IOS
                            )
                        );
                    }

                    if (attributesGroupedByName.has(RetailPriceAttributesEnum.GOOGLE_STANDALONE_MUSIC_RETAIL_PRICE)) {
                        rowsForOneTerritory.push(
                            generateRow(
                                attributesGroupedByName.get(
                                    RetailPriceAttributesEnum.GOOGLE_STANDALONE_MUSIC_RETAIL_PRICE
                                )?.value,
                                BundlePricingSubAttribute.GOOGLE
                            )
                        );
                    }

                    if (attributesGroupedByName.has(RetailPriceAttributesEnum.NON_MUSIC_RETAIL_PRICE)) {
                        rowsForOneTerritory.push(
                            generateRow(
                                attributesGroupedByName.get(RetailPriceAttributesEnum.NON_MUSIC_RETAIL_PRICE)?.value,
                                BundlePricingSubAttribute.NON_MUSIC
                            )
                        );
                    }
                });

                return territoryAccumulator.concat(rowsForOneTerritory);
            },
            [] as IBundlePricingRowData[]
        );

        return planAccumulator.concat(rowsForOnePlan);
    }, [] as IBundlePricingRowData[]);
};

export const bundlePricingSortModel: ColumnState[] = [
    { colId: 'territory', sort: 'asc', sortIndex: 0 },
    { colId: 'planName', sort: 'asc', sortIndex: 1 },
    { colId: 'planSubAttribute', sort: 'asc', sortIndex: 2 }
];
